<template>
  <v-app class="customer-screen-app">
    <!-- Fullscreen Button Overlay - Shows when not in fullscreen -->
    <transition name="fade">
      <div
        v-if="showFullscreenButton && !isFullscreen"
        class="fullscreen-prompt"
      >
        <div class="fullscreen-dialog">
          <v-icon size="48" color="primary" class="mb-3">fal fa-expand</v-icon>
          <h3 class="text-h5 mb-3">
            {{ $t("enter_fullscreen") || "Enter Fullscreen Mode" }}
          </h3>
          <p class="text-body-1 grey--text mb-4">
            {{
              $t("fullscreen_recommended") ||
              "Fullscreen mode is recommended for the best customer display experience."
            }}
          </p>
          <v-btn color="primary" large @click="enterFullscreen" class="mb-2">
            <v-icon left>fal fa-expand</v-icon>
            {{ $t("go_fullscreen") || "Go Fullscreen" }}
          </v-btn>
          <br />
          <v-btn text small @click="dismissFullscreenPrompt" class="mt-2">
            {{ $t("skip") || "Skip" }}
          </v-btn>
        </div>
      </div>
    </transition>

    <div class="customer-screen-container">
      <!-- Main Content -->
      <div class="main-content">
        <!-- Left Section (60% width) -->
        <div class="left-section">
          <!-- Dynamic QR Payment Section - Show when QR is active and has cart items -->
          <div
            v-if="isQRActive && defineStore.cart.length > 0 && qrCodeData"
            class="dynamic-qr-full-section"
          >
            <!-- QR Loading State -->
            <div v-if="qrLoading" class="qr-loading-state">
              <v-progress-circular
                indeterminate
                size="120"
                color="primary"
                class="mb-4"
              ></v-progress-circular>
              <div class="text-h4 mb-3">
                {{ $t("generating_qr_code") || "Generating QR Code..." }}
              </div>
              <div class="text-h6 grey--text">
                {{ $t("please_wait") || "Please wait..." }}
              </div>
            </div>

            <!-- QR Code Display - Show when waiting for payment -->
            <div v-else-if="qrCodeData" class="qr-payment-display">
              <div class="text-h4 mb-4 text-center">
                {{ $t("scan_qr_to_pay") || "Scan QR to Pay" }}
              </div>

              <!-- QR Code Image - Centered and Larger -->
              <div class="qr-container-center mb-5">
                <div class="qr-wrapper-center">
                  <img
                    :src="qrCodeData.qrCode"
                    alt="QR Code"
                    class="qr-image-center"
                  />
                  <div class="qr-overlay-center">
                    <img
                      src="/images/lao_qr.png"
                      alt="Lao QR"
                      class="lao-qr-overlay-center"
                      @error="handleLaoQRError"
                    />
                  </div>
                </div>
              </div>

              <!-- Payment Details -->
              <div class="payment-details-center">
                <div
                  class="text-h3 font-weight-bold primary--text mb-3 text-center"
                >
                  {{ qrCodeData.amount | NumberFormat }}
                </div>
                <div class="text-h6 grey--text mb-4 text-center">
                  {{ $t("bill_number") || "Bill Number" }}:
                  {{ qrCodeData.ticket_id }}
                </div>

                <!-- Payment Status -->
                <div class="payment-status-center">
                  <v-chip color="warning" outlined large class="mb-3">
                    <v-icon left>fal fa-clock</v-icon>
                    {{ $t("waiting_for_payment") || "Waiting for Payment" }}
                  </v-chip>
                </div>
              </div>
            </div>

            <!-- Payment Success State -->
            <div v-else-if="paymentSuccess" class="payment-success-state">
              <div class="success-icon mb-4">
                <v-icon size="120" color="success">fal fa-check-circle</v-icon>
              </div>
              <div
                class="text-h3 font-weight-bold success--text mb-3 text-center"
              >
                {{ $t("payment_successful") || "Payment Successful!" }}
              </div>
              <div class="text-h5 grey--text text-center mb-4">
                {{
                  $t("thank_you_for_payment") || "Thank you for your payment"
                }}
              </div>
              <div class="text-h6 grey--text text-center">
                {{
                  $t("receipt_will_be_printed") ||
                  "Receipt will be printed shortly"
                }}
              </div>
            </div>

            <!-- Initial State - Show when QR is active but no QR data yet -->
            <div v-else class="qr-initial-state">
              <div class="text-h4 mb-4 text-center">
                {{ $t("qr_payment_ready") || "QR Payment Ready" }}
              </div>
              <div class="text-h6 grey--text mb-5 text-center">
                {{
                  $t("merchant_will_generate_qr") ||
                  "Merchant will generate QR code for your order"
                }}
              </div>
              <div class="qr-placeholder-center">
                <v-icon size="120" color="grey lighten-2">fal fa-qrcode</v-icon>
              </div>
            </div>
          </div>

          <!-- Normal Layout - Show when QR is not active, no cart items, or no QR data -->
          <div v-else class="normal-layout">
            <!-- Image Slider (70% height) -->
            <div class="image-slider">
              <v-overlay v-if="loading" absolute>
                <v-progress-circular
                  indeterminate
                  size="64"
                ></v-progress-circular>
              </v-overlay>
              <v-carousel
                v-if="mainImages.length"
                cycle
                height="100%"
                hide-delimiter-background
                show-arrows
                interval="5000"
              >
                <v-carousel-item v-for="(item, i) in mainImages" :key="i">
                  <div style="position: relative; height: 100%">
                    <img
                      :src="IMAGES(item.img)"
                      class="slider-image"
                      alt="Promotion"
                    />
                    <div class="slider-overlay">
                      <h3>{{ item.title }}</h3>
                      <p>{{ item.desc }}</p>
                    </div>
                  </div>
                </v-carousel-item>
              </v-carousel>
              <div v-else class="slider-placeholder">
                <v-icon size="80" color="grey lighten-2">fal fa-image</v-icon>
                <p>No promotional images available</p>
              </div>
            </div>

            <!-- Marquee Text Section -->
            <div class="marquee-section">
              <div class="marquee-container">
                <div class="marquee-text">
                  <span>{{ $t("welcome_message") }}</span>
                  <span>{{ $t("payment_options_message") }}</span>
                  <span>{{ $t("service_message") }}</span>
                  <span>{{ $t("quality_message") }}</span>
                  <span>{{ $t("offers_message") }}</span>
                </div>
              </div>
            </div>

            <!-- QR Code Section (30% height) -->
            <div class="qr-section">
              <v-overlay v-if="loading" absolute>
                <v-progress-circular
                  indeterminate
                  size="64"
                ></v-progress-circular>
              </v-overlay>

              <!-- Static Bank QR Section -->
              <div class="static-qr-section">
                <h4 style="margin-bottom: 12px">
                  {{ $t("scan_to_pay") || "Scan to Pay" }}
                </h4>
                <div class="qr-grid">
                  <div v-for="(qr, i) in qrCodes" :key="i" class="qr-item">
                    <img
                      :src="IMAGES(qr.qr_code)"
                      :alt="qr.label"
                      class="qr-image"
                    />
                    <span class="qr-label">{{ qr.label }}</span>
                    <span
                      v-if="qr.bank_name"
                      class="qr-account-name text-h5 font-weight-bold text-capitalize"
                    >
                      {{ qr.bank_name }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Section (40% width) -->
        <div class="right-section">
          <!-- Product List (80% height) -->
          <div class="product-list-section">
            <div class="section-title">
              <div class="d-flex flex-column">
                <span>{{ $t("your_order") || "Your Order" }}</span>
                <span
                  v-if="defineStore.customerScreenOrderMeta"
                  class="text-caption grey--text"
                >
                  {{ defineStore.customerScreenOrderMeta.tableName }}
                  <template v-if="defineStore.customerScreenOrderMeta.orderCode">
                    - {{ defineStore.customerScreenOrderMeta.orderCode }}
                  </template>
                </span>
              </div>
              <v-chip v-if="defineStore.cart.length" small color="primary">
                {{ cartTotalQty }} {{ $t("items") || "items" }}
              </v-chip>
            </div>

            <div v-if="defineStore.cart.length" class="product-list">
              <v-list three-line>
                <template v-for="(item, index) in defineStore.cart">
                  <v-list-item
                    :key="
                      item.id +
                      '-' +
                      (item.variant
                        ? Object.values(item.variant.options).join('-')
                        : index)
                    "
                    class="item-cart"
                  >
                    <v-list-item-avatar
                      size="60"
                      tile
                      class="rounded-lg border grey lighten-2"
                    >
                      <app-img
                        width="60"
                        height="60"
                        class="rounded-lg"
                        :img="item.item.img"
                      />
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title
                        class="font-weight-bold text-h7 text-capitalize"
                      >
                        {{ getItemDisplayName(item) }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-capitalize">
                        {{ getItemCategoryName(item) }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="text-body-2 text-capitalize">
                        <span
                          class="text-body-2 text-capitalize primary--text font-weight-bold"
                        >
                          {{ getItemCurrencySymbol(item) }}
                          {{ item.price | NumberFormat }}
                        </span>
                        <!-- Show selected options as a single label if variant is present -->
                        <div
                          v-if="item.variant && item.variant.options"
                          class="mt-1"
                        >
                          <v-chip
                            x-small
                            color="grey"
                            outlined
                            class="mr-1 rounded-lg text-capitalize pa-1"
                          >
                            {{ Object.values(item.variant.options).join("-") }}
                          </v-chip>
                        </div>
                        <!-- Fallback: Show selected options as chips if present and no variant -->
                        <div
                          v-else-if="
                            (item.selectedVariationOptions &&
                              Object.keys(item.selectedVariationOptions)
                                .length > 0) ||
                            (item.variationOptions &&
                              Object.keys(item.variationOptions).length > 0)
                          "
                          class="mt-1"
                        >
                          <v-chip
                            v-for="(
                              option, key
                            ) in item.selectedVariationOptions ||
                            item.variationOptions"
                            :key="key"
                            x-small
                            color="primary"
                            outlined
                            class="mr-1 rounded-lg text-capitalize"
                          >
                            {{ key }}:
                            {{ option.value || option.name || option }}
                          </v-chip>
                        </div>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <!-- Quantity Display Only (No Controls) -->
                      <div class="quantity-display-only">
                        <div class="quantity-number">
                          {{ item.qty }}
                        </div>
                        <div class="quantity-label">
                          {{ $t("qty") || "Qty" }}
                        </div>
                      </div>
                    </v-list-item-action>
                  </v-list-item>

                  <v-divider
                    v-if="index < defineStore.cart.length - 1"
                    :key="index"
                    inset
                    class="my-0"
                  ></v-divider>
                </template>
              </v-list>
            </div>

            <div v-else class="empty-cart">
              <v-icon size="60" color="grey lighten-2"
                >fal fa-shopping-cart</v-icon
              >
              <p>{{ $t("cart_empty") || "Cart is empty" }}</p>
            </div>
          </div>

          <!-- Total Price (20% height) -->
          <div class="total-price-section">
            <div v-if="thbPrice !== '-'" class="price-row">
              <span>THB:</span>
              <span>{{ thbPrice }}</span>
            </div>
            <div v-if="usdPrice !== '-'" class="price-row">
              <span>USD:</span>
              <span>{{ usdPrice }}</span>
            </div>
            <div class="price-row total-row">
              <span>{{ $t("total") || "Total" }}:</span>
              <span class="total-amount">
                {{ getTotalCurrencySymbol() }}
                {{ defineStore.total_price | NumberFormat }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-app>
</template>

<script>
import { mainStore } from "@/stores/modules/cart";
import { mapGetters } from "vuex";
import { myMixin } from "@/mixin/main";
import PubNub from "pubnub";
import { pubnubConfig } from "@/utils/mock";
import { successPos } from "@/controllers/alert";
export default {
  mixins: [myMixin],
  data() {
    return {
      defineStore: mainStore(),
      mainImages: [],
      qrCodes: [],
      loading: false,
      // QR Payment related data
      currentTicketId: null,
      pubnub: new PubNub(pubnubConfig()),
      // Fullscreen state
      showFullscreenButton: false,
      fullscreenDismissed: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    cartTotalQty() {
      return this.defineStore.cart.reduce((total, item) => total + item.qty, 0);
    },
    thbPrice() {
      // Example: convert total_price to THB (replace with your logic)
      if (this.user && this.user.store_id && this.user.store_id.thb) {
        const thb = this.user.store_id.thb;
        return this.$options.filters.NumberFormat(
          this.defineStore.total_price / thb
        );
      }
      return "-";
    },
    usdPrice() {
      // Example: convert total_price to USD (replace with your logic)
      if (this.user && this.user.store_id && this.user.store_id.usd) {
        const usd = this.user.store_id.usd;
        return this.$options.filters.NumberFormat(
          this.defineStore.total_price / usd
        );
      }
      return "-";
    },
    isQRActive() {
      // Check if user and stores exist and QR is active
      return (
        this.user && this.user.stores && this.user.stores.is_active_qr === 1
      );
    },
    // QR state from Pinia store
    qrLoading() {
      return this.defineStore.customerScreenQR.loading;
    },
    qrCodeData() {
      return this.defineStore.customerScreenQR.qrData;
    },
    paymentSuccess() {
      return this.defineStore.customerScreenQR.paymentSuccess;
    },
    // Check if currently in fullscreen mode
    isFullscreen() {
      return (
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
    },
  },
  watch: {
    // Watch for QR state changes in Pinia store
    "defineStore.customerScreenQR": {
      handler(newState) {
        if (
          newState.qrData &&
          newState.qrData.ticket_id &&
          !this.currentTicketId
        ) {
          this.listenForPayment(newState.qrData.ticket_id);
        }

        if (newState.paymentSuccess) {
          setTimeout(() => {
            this.resetQRState();
          }, 5000);
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchCustomerDisplayData();
    this.setupPubNubListener();
    this.attemptFullscreen();
    this.setupFullscreenListener();
  },
  beforeDestroy() {
    // Clean up PubNub subscription
    if (this.currentTicketId) {
      try {
        this.pubnub.unsubscribe({ channels: [this.currentTicketId] });
      } catch (error) {
        console.warn("Error unsubscribing from channel:", error);
      }
    }
  },
  methods: {
    getItemDisplayName(cartItem) {
      const item = cartItem?.item || {};
      const locale = this.$i18n?.locale;
      let nameKey = "name_la";
      if (locale === "en") nameKey = "name_en";
      if (locale === "th") nameKey = "name_th";
      if (locale === "cn" || locale === "ch") nameKey = "name_cn";
      if (locale === "la") nameKey = "name_la";

      return item[nameKey] || item.name || "";
    },
    getItemCurrencySymbol(cartItem) {
      const symbol =
        cartItem?.currency?.symbol ||
        (cartItem?.currency?.code === "THB"
          ? "฿"
          : cartItem?.currency?.code === "LAK"
            ? "₭"
            : "");
      if (symbol) return symbol + " ";
      const fallback = this.defineStore?.currencySymbol?.symbol || "";
      return fallback ? fallback + " " : "";
    },
    getTotalCurrencySymbol() {
      if (this.defineStore?.cart?.length) {
        const first = this.defineStore.cart[0];
        const symbol =
          first?.currency?.symbol ||
          (first?.currency?.code === "THB"
            ? "฿"
            : first?.currency?.code === "LAK"
              ? "₭"
              : "");
        if (symbol) return symbol + " ";
      }
      const fallback = this.defineStore?.currencySymbol?.symbol || "";
      return fallback ? fallback + " " : "";
    },
    getItemCategoryName(cartItem) {
      const category = cartItem?.item?.category;
      if (!category || typeof category !== "object") {
        return "";
      }
      return this.$i18n.locale === "en"
        ? category.en_name || ""
        : category.la_name || "";
    },
    async fetchCustomerDisplayData() {
      try {
        this.loading = true;
        await Promise.all([this.fetchBanners(), this.fetchBankQRs()]);
      } catch (error) {
        console.error("Error fetching customer display data:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchBanners() {
      try {
        const { data } = await this.$http.get("get_customer_display_banners");

        if (data.status && data.data.length > 0) {
          this.mainImages = data.data;
        } else {
          // Fallback to default images if no banners found
          this.mainImages = [
            "/images/pos.png",
            "/images/pos.png",
            "/images/pos.png",
          ];
        }
      } catch (error) {
        console.error("Error fetching banners:", error);
      }
    },

    async fetchBankQRs() {
      try {
        const { data } = await this.$http.get("get_customer_display_bank_qrs");

        if (data.status && data.data.length > 0) {
          this.qrCodes = data.data;
        } else {
          // Fallback to default QR codes if no bank accounts found
          this.qrCodes = [
            {
              img: "https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=BankA",
              label: "Bank A",
            },
            {
              img: "https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=BankB",
              label: "Bank B",
            },
            {
              img: "https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=BankC",
              label: "Bank C",
            },
            {
              img: "https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=BankD",
              label: "Bank D",
            },
          ];
        }
      } catch (error) {
        console.error("Error fetching bank QR codes:", error);
      }
    },

    // Setup PubNub listener for real-time payment notifications
    setupPubNubListener() {
      this.pubnub.addListener({
        message: (event) => {
          // Handle QR payment success notification
          if (event.message === "qr_payment_success") {
            this.handleQRPaymentSuccess(event.channel);
          }
        },
      });
    },

    // Method to set QR data from parent/merchant (called when merchant generates QR)
    setQRData(qrData) {
      this.defineStore.setCustomerScreenQRData(qrData);

      // Listen for payment success on this ticket
      this.listenForPayment(qrData.ticket_id);
    },

    // Method to set loading state from parent/merchant
    setLoading(loading) {
      this.defineStore.setCustomerScreenQRLoading(loading);
    },

    // Listen for payment success on specific ticket
    async listenForPayment(ticket_id) {
      // Unsubscribe from previous channel if exists
      if (this.currentTicketId) {
        try {
          this.pubnub.unsubscribe({ channels: [this.currentTicketId] });
        } catch (error) {
          console.warn("Error unsubscribing from previous channel:", error);
        }
      }

      // Store current ticket ID
      this.currentTicketId = ticket_id;

      // Subscribe to payment channel for this ticket
      try {
        this.pubnub.subscribe({ channels: [ticket_id] });
        console.log(`Listening for payment on ticket: ${ticket_id}`);
      } catch (error) {
        console.error("Error subscribing to payment channel:", error);
      }
    },

    // Handle QR payment success
    async handleQRPaymentSuccess(ticket_id) {
      console.log(`Payment successful for ticket: ${ticket_id}`);

      // Show success state using Pinia store
      this.defineStore.setCustomerScreenPaymentSuccess(true);

      // get ticket detail
      const { data } = await this.$http.get(`bill_detail/${ticket_id}`);

      await successPos(this, data.data, false);
      // Play success sound if available
      if (typeof this.playAudio === "function") {
        this.playAudio();
      }

      // Reset after 5 seconds
      setTimeout(() => {
        this.resetQRState();
      }, 5000);
    },

    // Reset QR state
    resetQRState() {
      // Reset using Pinia store
      this.defineStore.resetCustomerScreenQR();

      // Unsubscribe from current channel
      if (this.currentTicketId) {
        try {
          this.pubnub.unsubscribe({ channels: [this.currentTicketId] });
          this.currentTicketId = null;
        } catch (error) {
          console.warn("Error unsubscribing from channel:", error);
        }
      }
    },

    // Handle Lao QR overlay error
    handleLaoQRError() {
      console.error("Lao QR image failed to load. Using a placeholder.");
    },

    // Fullscreen methods
    async attemptFullscreen() {
      // Check if fullscreen was previously dismissed
      const dismissed = localStorage.getItem(
        "customerScreenFullscreenDismissed"
      );
      if (dismissed === "true") {
        this.fullscreenDismissed = true;
        return;
      }

      // Small delay to ensure page is fully loaded
      await new Promise((resolve) => setTimeout(resolve, 800));

      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
          console.log("Fullscreen entered successfully");
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
          console.log("Webkit fullscreen entered successfully");
        } else if (elem.mozRequestFullScreen) {
          await elem.mozRequestFullScreen();
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
        }
      } catch (error) {
        console.log(
          "Auto-fullscreen failed (user gesture required):",
          error.message
        );
        // Show the fullscreen button as fallback
        this.showFullscreenButton = true;
      }
    },

    async enterFullscreen() {
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        } else if (elem.mozRequestFullScreen) {
          await elem.mozRequestFullScreen();
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
        }
        this.showFullscreenButton = false;
      } catch (error) {
        console.error("Failed to enter fullscreen:", error);
      }
    },

    dismissFullscreenPrompt() {
      this.showFullscreenButton = false;
      this.fullscreenDismissed = true;
      localStorage.setItem("customerScreenFullscreenDismissed", "true");
    },

    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },

    setupFullscreenListener() {
      // Listen for fullscreen changes
      document.addEventListener(
        "fullscreenchange",
        this.handleFullscreenChange
      );
      document.addEventListener(
        "webkitfullscreenchange",
        this.handleFullscreenChange
      );
      document.addEventListener(
        "mozfullscreenchange",
        this.handleFullscreenChange
      );
      document.addEventListener(
        "MSFullscreenChange",
        this.handleFullscreenChange
      );
    },

    handleFullscreenChange() {
      // Hide the prompt when entering fullscreen
      if (this.isFullscreen) {
        this.showFullscreenButton = false;
      }
    },
  },
};
</script>

<style scoped>
.customer-screen-app {
  height: 100vh;
  overflow: hidden;
}

.customer-screen-container {
  height: 100vh;
  width: 100vw;
  display: flex;
  background: #f5f5f5;
}

.main-content {
  width: 100%;
  height: 100%;
  display: flex;
  gap: 0;
}

/* Left Section (60% width) */
.left-section {
  width: 60%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
}

/* Image Slider (70% height) */
.image-slider {
  height: 70%;
  background: white;
  overflow: hidden;
  position: relative;
}

.slider-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
}

.slider-placeholder p {
  margin-top: 16px;
  font-size: 1.1rem;
}

.slider-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slider-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  padding: 20px;
}

/* QR Code Section (30% height) */
.qr-section {
  height: 30%;
  background: white;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-top: 1px solid #e0e0e0;
}

.qr-section h4 {
  margin: 0 0 12px 0;
  font-size: 1.2rem;
  color: #333;
}

/* Full Screen Dynamic QR Section - Replaces banner and QR section */
.dynamic-qr-full-section {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #b76eeb23 100%);
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

/* Normal Layout - When QR is not active */
.normal-layout {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Marquee Section */
.marquee-section {
  background: linear-gradient(90deg, #5b39c974, #7c4dff6a);
  padding: 12px 0;
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
}

.marquee-container {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
}

.marquee-text {
  display: inline-block;
  animation: marquee 30s linear infinite;
  white-space: nowrap;
}

.marquee-text span {
  display: inline-block;
  margin-right: 50px;
  color: white;
  font-weight: 500;
  font-size: 14px;
}

@keyframes marquee {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
}

.qr-loading-state {
  text-align: center;
}

/* Centered QR Code Styles for Full Screen */
.qr-container-center {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
}

.qr-wrapper-center {
  position: relative;
  width: 400px;
  height: 400px;
  border: 3px solid #5b39c9;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(122, 27, 224, 0.227);
  background: white;
}

.qr-image-center {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.qr-overlay-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.lao-qr-overlay-center {
  width: 40px;
  height: 40px;
  object-fit: contain;
  background-color: #fff;
  border-radius: 6px;
  padding: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.payment-details-center {
  padding: 32px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  margin-top: 32px;
  text-align: center;
  min-width: 450px;
  border: 2px solid #5b39c992;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.payment-status-center {
  text-align: center;
  margin-top: 16px;
}

.qr-placeholder-center {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.qr-payment-display {
  text-align: center;
  width: 100%;
}

.payment-success-state {
  text-align: center;
  width: 100%;
}

.success-icon {
  display: flex;
  justify-content: center;
}

.qr-initial-state {
  text-align: center;
  width: 100%;
}

/* Static QR Section */
.static-qr-section {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.qr-grid {
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.qr-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-image {
  width: 150px;
  height: 150px;
  border-radius: 8px;
  margin-bottom: 10px;
}

.qr-label {
  font-size: 12px;
  color: #555;
  text-align: center;
  font-weight: 600;
  margin-bottom: 2px;
}

.qr-account-name {
  font-size: 10px;
  color: #666;
  text-align: center;
  margin-bottom: 1px;
}

.qr-account-no {
  font-size: 9px;
  color: #777;
  text-align: center;
}

.qr-instruction {
  margin: 12px 0 0 0;
  color: #666;
  font-size: 14px;
}

/* Right Section (40% width) */
.right-section {
  width: 40%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border-left: 1px solid #e0e0e0;
}

/* Product List (80% height) */
.product-list-section {
  height: 90%;
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 1.3rem;
  color: #333;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-list {
  flex: 1;
  overflow-y: auto;
}

.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}

.empty-cart p {
  margin-top: 16px;
  font-size: 1.1rem;
}

/* Cart Item Styles */
.item-cart {
  transition: all 0.3s ease;
  cursor: pointer !important;
  border-radius: 10px;
  padding: 8px 0;
}

.item-cart:hover {
  background-color: #5b39c90e !important;
}

/* Quantity Display Only (No Controls) */
.quantity-display-only {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 16px;
  padding: 8px 12px;
  min-width: 60px;
  border: 1px solid #e0e0e0;
}

.quantity-number {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

.quantity-label {
  font-size: 10px;
  color: #666;
  margin-top: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.v-list-item-action {
  margin: 0;
  min-width: auto;
}

/* Total Price (20% height) */
.total-price-section {
  height: 10%;
  background: #f8f9fa;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 1rem;
}

.price-row:last-child {
  margin-bottom: 0;
}

.total-row {
  padding-top: 12px;
  font-weight: bold;
  font-size: 1.2rem;
}

.total-amount {
  color: #5b39c9;
  font-weight: 700;
}

/* Custom scrollbar */
.product-list::-webkit-scrollbar {
  width: 6px;
}

.product-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.product-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.product-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Fullscreen Prompt Overlay */
.fullscreen-prompt {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.fullscreen-dialog {
  background: white;
  border-radius: 16px;
  padding: 40px 60px;
  text-align: center;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
